import { useState, useEffect, useMemo, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import configDetails from '../Config/Config';
import {
  generateOutlookAuthUrl,exchangeCodeOnBackend,fetchMeetingsFromBackend,
} from '../utils/outlookAuth';

const useCalendar = ({ activeTab, setNotification }) => {
  // Google Calendar states
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [calendarConnected, setCalendarConnected] = useState(false);

  // Outlook Calendar states
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [isProcessingOutlookRedirect, setIsProcessingOutlookRedirect] = useState(false);
  const [outlookNeedsReauth, setOutlookNeedsReauth] = useState(false);

  // Selected date for calendar date-picker filtering
  const [selectedDate, setSelectedDate] = useState(new Date());

  const showNotification = useCallback((msg, type = 'success') => {
    setNotification({ message: msg, type });
    setTimeout(() => setNotification(null), 3000);
  }, [setNotification]);

  // --- Google Calendar ---

  const fetchCalendarEvents = useCallback(async (tokenParam, showLoading = true, retried = false) => {
    try {
      if (showLoading) {
        setEventsLoading(true);
      }
      setEventsError(null);

      let token = tokenParam || localStorage.getItem('google_access_token');

      if (!token) {
        setEventsError('Google account not connected');
        return;
      }

      const now = new Date().toISOString();

      const response = await fetch(
        `${configDetails.calendarApiUrl}?timeMin=${now}&maxResults=20&singleEvents=true&orderBy=startTime`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (response.status === 401) {
        if (!retried) {
          try {
            showNotification('Google session expired. Please reconnect.', 'warning');
            setCalendarConnected(false);
            localStorage.removeItem('google_access_token');
            return;
          } catch (refreshErr) {
          showNotification('Google token refresh failed:', refreshErr);
          }
        }
        setCalendarConnected(false);
        localStorage.removeItem('google_access_token');
        setEventsError('Google session expired. Please reconnect.');
        return;
      }

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to fetch events');
      }

      setEvents((data.items || []).map(e => ({ ...e, source: 'google' })));
    } catch (error) {
      setEventsError('Unable to load calendar events');
    } finally {
      if (showLoading) {
        setEventsLoading(false);
      }
    }
  }, [showNotification]);

  // Web-based Google Sign-In using implicit flow (same as GoogleLoginButton)
  const googleLogin = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/calendar.readonly',
    onSuccess: async (tokenResponse) => {
      try {
        const accessToken = tokenResponse.access_token;
        localStorage.setItem('google_access_token', accessToken);
        setCalendarConnected(true);
        showNotification('Google Calendar connected');
        fetchCalendarEvents(accessToken);

        const pendingSwitch = localStorage.getItem('pending_provider_switch');
        if (pendingSwitch === 'outlook_to_google') {
          localStorage.removeItem('pending_provider_switch');
          localStorage.removeItem('outlook_connected');
          setOutlookEvents([]);
          setOutlookConnected(false);
        }
      } catch (error) {
        showNotification('Failed to connect Google Calendar', 'error');
      }
    },
    onError: (error) => {
      localStorage.removeItem('pending_provider_switch');
      if (error?.error !== 'popup_closed_by_user') {
        showNotification('Failed to connect Google Calendar', 'error');
      }
    },
  });

  const connectGoogleCalendar = useCallback(async () => {
    try {
      googleLogin();
      return true;
    } catch (error) {
      showNotification('Failed to connect Google Calendar', 'error');
      return false;
    }
  }, [googleLogin, showNotification]);

  const disconnectGoogleCalendar = useCallback(async () => {
    try {
      localStorage.removeItem('google_access_token');
      localStorage.removeItem('google_refresh_token');
      setEvents([]);
      setEventsError(null);
      setCalendarConnected(false);
      showNotification('Google Calendar disconnected', 'success');
    } catch (error) {
      showNotification('Failed to disconnect Google Calendar', 'error');
    }
  }, [showNotification]);

  // --- Outlook Calendar ---

  const fetchOutlookCalendarEvents = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setEventsLoading(true);
      }

      const result = await fetchMeetingsFromBackend();

      if (result.connected) {
        setOutlookConnected(true);
        setOutlookNeedsReauth(false);
        setOutlookEvents(result.events);
      } else {
        setOutlookConnected(false);
        setOutlookEvents([]);
        setOutlookNeedsReauth(!!result.needsReauth);
        if (result.error) {
          showNotification(result.error, 'error');
        }
      }
    } catch (error) {
      showNotification('Failed to fetch Outlook calendar events', 'error');
      setOutlookConnected(false);
      setOutlookEvents([]);
    } finally {
      if (showLoading) {
        setEventsLoading(false);
      }
    }
  }, [showNotification]);

  // Full-page redirect to Microsoft login — no iframe/modal
  const connectOutlookCalendar = useCallback(async () => {
    try {
      const { url, codeVerifier } = await generateOutlookAuthUrl(configDetails.outlookClientId);
      // Persist verifier in localStorage so it survives the redirect
      localStorage.setItem('outlook_code_verifier', codeVerifier);
      // Clear any stale reauth flag — a fresh OAuth attempt is starting
      setOutlookNeedsReauth(false);
      // Navigate away — Microsoft's OAuth does not work inside iframes
      window.location.href = url;
    } catch (error) {
      showNotification('Failed to start Outlook Calendar connection', 'error');
    }
  }, [showNotification]);

  const disconnectOutlookCalendar = useCallback(async () => {
    try {
      setOutlookEvents([]);
      setOutlookConnected(false);
      localStorage.removeItem('outlook_connected');
      showNotification('Outlook Calendar disconnected', 'success');
    } catch (error) {
      showNotification('Failed to disconnect Outlook Calendar', 'error');
    }
  }, [showNotification]);

  // --- Switch ---

  const switchToOutlookCalendar = useCallback(async () => {
    try {
      if (calendarConnected) {
        const token = localStorage.getItem('google_access_token');
        if (token) localStorage.setItem('pre_switch_google_token', token);
        const refresh = localStorage.getItem('google_refresh_token');
        if (refresh) localStorage.setItem('pre_switch_google_refresh_token', refresh);
        localStorage.setItem('pending_provider_switch', 'google_to_outlook');
      }
      await connectOutlookCalendar();
    } catch (error) {
      localStorage.removeItem('pending_provider_switch');
      localStorage.removeItem('pre_switch_google_token');
      localStorage.removeItem('pre_switch_google_refresh_token');
      showNotification('Failed to switch to Outlook Calendar', 'error');
    }
  }, [connectOutlookCalendar, calendarConnected, showNotification]);

  const switchToGoogleCalendar = useCallback(async () => {
    try {
      if (outlookConnected) {
        localStorage.setItem('pending_provider_switch', 'outlook_to_google');
      }
      await connectGoogleCalendar();
    } catch (error) {
      localStorage.removeItem('pending_provider_switch');
      showNotification('Failed to switch to Google Calendar', 'error');
    }
  }, [connectGoogleCalendar, outlookConnected, showNotification]);

  // --- Derived state ---

  const mergedEvents = useMemo(() => {
    const all = [
      ...(calendarConnected ? events : []),
      ...(outlookConnected ? outlookEvents : []),
    ];
    all.sort((a, b) => {
      const toMs = (e) => {
        const dt = e.start?.dateTime;
        const d = e.start?.date;
        if (dt) return new Date(dt).getTime();
        if (d) { const [y,m,day] = d.split('-').map(Number); return new Date(y, m-1, day).getTime(); }
        return 0;
      };
      return toMs(a) - toMs(b);
    });
    return all;
  }, [events, outlookEvents, calendarConnected, outlookConnected]);

  const anyCalendarConnected = calendarConnected || outlookConnected;

  // Parse event start as local Date — handles both "2026-06-17" (date-only) and full ISO dateTime
  const parseEventDate = (event) => {
    const dt = event.start?.dateTime;
    const d = event.start?.date;
    if (dt) return new Date(dt);
    if (d) {
      // Date-only string like "2026-06-17" — parse as LOCAL midnight, not UTC
      const [year, month, day] = d.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return null;
  };

  // Events from the selected date onwards — only timed events (dateTime present)
  const eventsForDate = useMemo(() => {
    if (!selectedDate) return mergedEvents;
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    return mergedEvents.filter(event => {
      // Skip all-day / unscheduled events that have no specific time
      if (!event.start?.dateTime) return false;
      const eventDate = parseEventDate(event);
      return eventDate && eventDate >= startOfDay;
    });
  }, [mergedEvents, selectedDate]);

  // Selected-day events (timed events whose date matches selectedDate)
  const todayEvents = useMemo(() => {
    const base = selectedDate || new Date();
    const selectedKey = `${base.getFullYear()}-${base.getMonth()}-${base.getDate()}`;
    return mergedEvents.filter(event => {
      if (!event.start?.dateTime) return false;
      const d = parseEventDate(event);
      if (!d) return false;
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` === selectedKey;
    });
  }, [mergedEvents, selectedDate]);

  // Upcoming events — timed events strictly after the selected date
  const upcomingEvents = useMemo(() => {
    const base = selectedDate || new Date();
    const endOfSelectedDay = new Date(base);
    endOfSelectedDay.setHours(23, 59, 59, 999);
    return mergedEvents.filter(event => {
      if (!event.start?.dateTime) return false;
      const d = parseEventDate(event);
      return d && d > endOfSelectedDay;
    });
  }, [mergedEvents, selectedDate]);

  // Set of date keys that have at least one timed event — used for dot markers
  const datesWithEvents = useMemo(() => {
    const set = new Set();
    mergedEvents.forEach(event => {
      if (!event.start?.dateTime) return; // skip all-day events
      const d = parseEventDate(event);
      if (d) set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    });
    return set;
  }, [mergedEvents]);

  // --- Restore connections on mount ---

  useEffect(() => {
    const checkGoogleConnection = async () => {
      try {
        const token = localStorage.getItem('google_access_token');
        const pendingSwitch = localStorage.getItem('pending_provider_switch');
        if (token && pendingSwitch !== 'google_to_outlook') {
          setCalendarConnected(true);
          fetchCalendarEvents(token);
        }
      } catch (error) {
        // silently ignore
      }
    };
    checkGoogleConnection();
  }, [fetchCalendarEvents]);

  // Check Outlook connection by calling the backend API
  useEffect(() => {
    const checkOutlookConnection = async () => {
      try {
        const result = await fetchMeetingsFromBackend();
        if (result.connected) {
          setOutlookConnected(true);
          setOutlookNeedsReauth(false);
          setOutlookEvents(result.events);
        } else {
          setOutlookNeedsReauth(!!result.needsReauth);
        }
      } catch (error) {
        setOutlookConnected(false);
      }
    };
    checkOutlookConnection();
  }, []); // Only on mount

  // --- Handle Outlook redirect on page load ---
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDesc = urlParams.get('error_description');
    const storedVerifier = localStorage.getItem('outlook_code_verifier');

    // Nothing to process — normal app load
    if (!code && !error) {
      const pendingSwitch = localStorage.getItem('pending_provider_switch');
      if (pendingSwitch === 'google_to_outlook') {
        const savedToken = localStorage.getItem('pre_switch_google_token');
        const savedRefresh = localStorage.getItem('pre_switch_google_refresh_token');
        if (savedToken) {
          localStorage.setItem('google_access_token', savedToken);
          if (savedRefresh) localStorage.setItem('google_refresh_token', savedRefresh);
          setCalendarConnected(true);
          fetchCalendarEvents(savedToken);
        }
        localStorage.removeItem('pending_provider_switch');
        localStorage.removeItem('pre_switch_google_token');
        localStorage.removeItem('pre_switch_google_refresh_token');
      }
      return;
    }

    // Always clean the URL immediately to prevent re-processing on re-renders
    window.history.replaceState({}, document.title, window.location.pathname);

    // Helper: restore Google if a switch was in progress
    const restoreGoogleAfterFailedSwitch = () => {
      const pendingSwitch = localStorage.getItem('pending_provider_switch');
      if (pendingSwitch === 'google_to_outlook') {
        const savedToken = localStorage.getItem('pre_switch_google_token');
        const savedRefresh = localStorage.getItem('pre_switch_google_refresh_token');
        if (savedToken) {
          localStorage.setItem('google_access_token', savedToken);
          if (savedRefresh) localStorage.setItem('google_refresh_token', savedRefresh);
          setCalendarConnected(true);
          fetchCalendarEvents(savedToken);
        }
        localStorage.removeItem('pending_provider_switch');
        localStorage.removeItem('pre_switch_google_token');
        localStorage.removeItem('pre_switch_google_refresh_token');
      }
    };

    if (error) {
      localStorage.removeItem('outlook_code_verifier');
      restoreGoogleAfterFailedSwitch();
      showNotification(errorDesc || 'Outlook authentication failed', 'error');
      return;
    }

    if (!storedVerifier) {
      restoreGoogleAfterFailedSwitch();
      showNotification('Authentication session expired. Please try again.', 'error');
      return;
    }

    (async () => {
      try {
        setIsProcessingOutlookRedirect(true);

        // Exchange the OAuth code via backend (handles Microsoft token exchange + storage)
        await exchangeCodeOnBackend(code, storedVerifier);

        localStorage.removeItem('outlook_code_verifier');

        // Outlook connected successfully — NOW disconnect Google if this was a switch
        const pendingSwitch = localStorage.getItem('pending_provider_switch');
        if (pendingSwitch === 'google_to_outlook') {
          localStorage.removeItem('google_access_token');
          localStorage.removeItem('google_refresh_token');
          localStorage.removeItem('pre_switch_google_token');
          localStorage.removeItem('pre_switch_google_refresh_token');
          localStorage.removeItem('pending_provider_switch');
          setEvents([]);
          setCalendarConnected(false);
        }

        localStorage.setItem('outlook_connected', 'true');
        setOutlookConnected(true);
        setOutlookNeedsReauth(false);
        showNotification('Outlook Calendar connected');
        // Fetch meetings from backend
        const result = await fetchMeetingsFromBackend();
        if (result.connected) {
          setOutlookEvents(result.events);
        }
      } catch (err) {
        localStorage.removeItem('outlook_code_verifier');
        restoreGoogleAfterFailedSwitch();

        let errorMessage = 'Failed to connect Outlook Calendar';
        if (err.message?.includes('expired')) {
          errorMessage = 'Authentication code expired. Please try again.';
        } else if (err.message?.includes('timeout')) {
          errorMessage = 'Connection timeout. Please try again.';
        } else if (err.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection.';
        }
        showNotification(errorMessage, 'error');
      } finally {
        setIsProcessingOutlookRedirect(false);
      }
    })();
  }, []); 
  return {
    // State
    events,
    eventsLoading,
    eventsError,
    calendarConnected,
    outlookConnected,
    outlookNeedsReauth,
    outlookEvents,
    mergedEvents,
    anyCalendarConnected,
    isProcessingOutlookRedirect,
    selectedDate,
    setSelectedDate,
    eventsForDate,
    todayEvents,
    upcomingEvents,
    datesWithEvents,

    // Actions
    fetchCalendarEvents,
    connectGoogleCalendar,
    disconnectGoogleCalendar,
    connectOutlookCalendar,
    fetchOutlookCalendarEvents,
    disconnectOutlookCalendar,
    switchToOutlookCalendar,
    switchToGoogleCalendar,
  };
};

export default useCalendar;